import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useFormik } from "formik";
import * as Yup from "yup";

import { DEFAULT_PRODUCT_IMAGE } from "../../constants/images";
import { AppDispatch } from "../../store";
import { showAlertWithTimeout } from "../../slices/Alert/reducer";
import { getMenus, getMenuOptions, updateMenu } from "../../slices/thunks";
import { initialState as menuOptionsInitialState } from "../../slices/MenuOptions/reducer";
import {
    buildMenuOptionChoices,
    extractMenuOptionIdsFromMenu,
    extractMenuOptionListFromMenu,
    getMenuOptionName,
    getMenuOptionIsActive,
    getMenuOptionSelectionType,
    MenuOptionSelectChoice,
    toComparableMenuOptionId,
} from "../../helpers/menuOptions";
import { getMenuDetail, getMenuOptionValues } from "../../helpers/backend_helper";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";

interface MenuFormValues {
    name: string;
    price: string;
    description: string;
    image: string;
    categories: string[];
    available: boolean;
    optionSelections: string[];
}

interface OptionValueItem {
    id: string | number;
    label: string;
    isAvailable: boolean;
    priceModifier: string;
    isDefault: boolean;
}

interface OptionValueGroup {
    optionId: string | number;
    name: string;
    selectionType: "single" | "multiple";
    values: OptionValueItem[];
}

const mergeOptionValueItems = (
    existing: OptionValueItem[],
    incoming: OptionValueItem[],
    selectionType: "single" | "multiple"
): OptionValueItem[] => {
    const existingMap = new Map<string, OptionValueItem>();
    existing.forEach((item) => {
        existingMap.set(String(item.id), item);
    });

    const incomingIds = new Set<string>();
    const merged = incoming.map((item) => {
        const key = String(item.id);
        incomingIds.add(key);
        const match = existingMap.get(key);
        if (!match) {
            return item;
        }
        return {
            ...item,
            priceModifier:
                match.priceModifier !== undefined ? match.priceModifier : item.priceModifier,
            isDefault: match.isDefault !== undefined ? match.isDefault : item.isDefault,
        };
    });

    existing.forEach((item) => {
        const key = String(item.id);
        if (!incomingIds.has(key)) {
            merged.push(item);
        }
    });

    if (selectionType === "single" && merged.length > 0) {
        const hasDefault = merged.some((item) => item.isDefault);
        if (!hasDefault) {
            merged[0] = { ...merged[0], isDefault: true };
        }
    }

    return merged;
};

const mergeOptionValueGroup = (
    existing: OptionValueGroup,
    incoming: OptionValueGroup
): OptionValueGroup => {
    const selectionType = existing.selectionType || incoming.selectionType;
    return {
        optionId: existing.optionId ?? incoming.optionId,
        name: existing.name || incoming.name,
        selectionType,
        values: mergeOptionValueItems(existing.values, incoming.values, selectionType),
    };
};

interface FoundMenuInfo {
    menu: any;
    category?: any;
}

const toComparableId = (value: string | number | undefined | null): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }

    return String(value);
};

const extractOptionValues = (payload: any): any[] => {
    if (!payload) {
        return [];
    }

    const data = payload?.data ?? payload;
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.data)) {
        return data.data;
    }

    if (Array.isArray(data?.option_values)) {
        return data.option_values;
    }

    return [];
};

const getMenuIdentifier = (menu: any): string | null => {
    if (!menu || typeof menu !== "object") {
        return null;
    }

    const candidates = [
        (menu as any).id,
        (menu as any).menuId,
        (menu as any).menu_id,
        (menu as any).uuid,
        (menu as any).slug,
        (menu as any).code,
    ];

    for (const value of candidates) {
        if (value !== undefined && value !== null && value !== "") {
            return String(value);
        }
    }

    return null;
};

const parseBooleanLike = (value: any): boolean | undefined => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        if (Number.isNaN(value)) {
            return undefined;
        }
        return value !== 0;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized.length === 0) {
            return undefined;
        }

        const truthyValues = ["true", "1", "available", "in_stock", "instock", "active", "on"];
        const falsyValues = ["false", "0", "unavailable", "out_of_stock", "inactive", "off", "sold_out"];

        if (truthyValues.includes(normalized)) {
            return true;
        }

        if (falsyValues.includes(normalized)) {
            return false;
        }
    }

    return undefined;
};

const getMenuAvailabilityFromData = (menu: any): boolean => {
    if (!menu || typeof menu !== "object") {
        return true;
    }

    const candidates = [
        (menu as any).isAvailable,
        (menu as any).is_available,
        (menu as any).available,
        (menu as any).availability,
        (menu as any).status,
        (menu as any).inStock,
        (menu as any).in_stock,
    ];

    for (const candidate of candidates) {
        const parsed = parseBooleanLike(candidate);
        if (parsed !== undefined) {
            return parsed;
        }
    }

    return true;
};

const getMenuNameFromData = (menu: any): string => {
    if (!menu || typeof menu !== "object") {
        return "";
    }

    const candidates = [
        (menu as any).name,
        (menu as any).title,
        (menu as any).productName,
        (menu as any).displayName,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim().length > 0) {
            return candidate;
        }
    }

    return "";
};

const getMenuPriceFromData = (menu: any): number | undefined => {
    if (!menu || typeof menu !== "object") {
        return undefined;
    }

    const candidates = [
        (menu as any).price,
        (menu as any).basePrice,
        (menu as any).base_price,
        (menu as any).cost,
        (menu as any).amount,
    ];

    for (const candidate of candidates) {
        if (candidate === undefined || candidate === null) {
            continue;
        }

        if (typeof candidate === "number" && !Number.isNaN(candidate)) {
            return candidate;
        }

        const numeric = Number(candidate);
        if (!Number.isNaN(numeric)) {
            return numeric;
        }
    }

    return undefined;
};

const formatPriceValue = (price: number | undefined): string => {
    if (price === undefined || Number.isNaN(price)) {
        return "";
    }

    return String(price);
};

const getMenuDescriptionFromData = (menu: any): string => {
    if (!menu || typeof menu !== "object") {
        return "";
    }

    const candidates = [
        (menu as any).description,
        (menu as any).desc,
        (menu as any).details,
        (menu as any).summary,
        (menu as any).note,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string") {
            return candidate;
        }
    }

    return "";
};

const getCategoryDisplayName = (category: any): string => {
    if (!category || typeof category !== "object") {
        return "";
    }

    const candidates = [
        (category as any).name,
        (category as any).title,
        (category as any).label,
        (category as any).displayName,
        (category as any).slug,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim().length > 0) {
            return candidate;
        }
    }

    return "";
};

const getMenuCategoryFromData = (menu: any, fallbackCategory?: any): string[] => {
    if (!menu || typeof menu !== "object") {
        const fallbackName = fallbackCategory ? getCategoryDisplayName(fallbackCategory) : "";
        return fallbackName ? [fallbackName] : [];
    }

    if (Array.isArray((menu as any).categories)) {
        const names = (menu as any).categories
            .map((category: any) => getCategoryDisplayName(category))
            .filter((name: string) => name.trim().length > 0);
        if (names.length > 0) {
            return Array.from(new Set(names));
        }
    }

    if (typeof (menu as any).category === "string") {
        return [(menu as any).category as string];
    }

    if ((menu as any).category && typeof (menu as any).category === "object") {
        const categoryInfo = (menu as any).category;
        const name = getCategoryDisplayName(categoryInfo);
        if (name) {
            return [name];
        }
    }

    if (typeof (menu as any).categoryName === "string") {
        return [(menu as any).categoryName as string];
    }

    if (typeof (menu as any).category_name === "string") {
        return [(menu as any).category_name as string];
    }

    const fallbackName = fallbackCategory ? getCategoryDisplayName(fallbackCategory) : "";
    return fallbackName ? [fallbackName] : [];
};

const getMenuImageFromData = (menu: any): string => {
    if (!menu || typeof menu !== "object") {
        return "";
    }

    const candidates = [
        (menu as any).image,
        (menu as any).imageUrl,
        (menu as any).image_url,
        (menu as any).thumbnail,
        (menu as any).thumbnailUrl,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim().length > 0) {
            return candidate;
        }
    }

    return "";
};

const findMenuById = (
    menuId: string | undefined,
    menus: any[],
    categories: any[]
): FoundMenuInfo | undefined => {
    if (!menuId) {
        return undefined;
    }

    const comparableId = toComparableId(menuId);

    const directMenu = menus.find((menu) => toComparableId(getMenuIdentifier(menu)) === comparableId);
    if (directMenu) {
        return { menu: directMenu };
    }

    for (const category of categories) {
        if (!category || typeof category !== "object" || !Array.isArray((category as any).menus)) {
            continue;
        }

        const found = (category as any).menus.find(
            (menu: any) => toComparableId(getMenuIdentifier(menu)) === comparableId
        );

        if (found) {
            return {
                menu: found,
                category,
            };
        }
    }

    return undefined;
};

const extractMenuDetailFromResponse = (payload: any): any | null => {
    if (!payload) {
        return null;
    }

    if (Array.isArray(payload)) {
        return payload[0] ?? null;
    }

    if (payload && typeof payload === "object") {
        if ((payload as any).menu && typeof (payload as any).menu === "object") {
            return (payload as any).menu;
        }

        if ((payload as any).data && typeof (payload as any).data === "object") {
            return extractMenuDetailFromResponse((payload as any).data);
        }

        if ((payload as any).response && typeof (payload as any).response === "object") {
            return extractMenuDetailFromResponse((payload as any).response);
        }
    }

    return payload;
};

const validationSchema = Yup.object({
    name: Yup.string().trim().required("Vui lòng nhập tên món"),
    price: Yup.number()
        .typeError("Giá phải là số hợp lệ")
        .min(0, "Giá không được âm")
        .required("Vui lòng nhập giá"),
    description: Yup.string().trim(),
    image: Yup.string()
        .trim()
        .transform((value) => (value === "" ? undefined : value))
        .url("Đường dẫn ảnh không hợp lệ")
        .notRequired()
        .nullable(),
    categories: Yup.array()
        .of(Yup.string().trim())
        .min(1, "Vui lòng nhập ít nhất 1 danh mục")
        .required("Vui lòng nhập ít nhất 1 danh mục"),
    available: Yup.boolean().required(),
    optionSelections: Yup.array().of(Yup.string().trim()),
});

const EditMenu = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { menuId } = useParams<{ menuId: string }>();
    const { menus, categories } = useSelector((state: any) => state.MenuList);

    const menusList = useMemo(() => (Array.isArray(menus) ? menus : []), [menus]);
    const categoriesList = useMemo(() => (Array.isArray(categories) ? categories : []), [categories]);

    const menuInfo = useMemo(
        () => findMenuById(menuId, menusList, categoriesList),
        [menuId, menusList, categoriesList]
    );
    const existingMenu = menuInfo?.menu;
    const parentCategory = menuInfo?.category;

    const menuOptionsState = useSelector(
        (state: any) => state.MenuOptions ?? menuOptionsInitialState
    );
    const menuOptionsList = useMemo(
        () => (Array.isArray(menuOptionsState.options) ? menuOptionsState.options : []),
        [menuOptionsState.options]
    );

    const [hasRequestedMenus, setHasRequestedMenus] = useState(false);
    const [isFetchingMenu, setIsFetchingMenu] = useState(false);
    const [detailData, setDetailData] = useState<any | null>(null);
    const [isFetchingDetail, setIsFetchingDetail] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [optionValueGroups, setOptionValueGroups] = useState<
        Record<string, OptionValueGroup>
    >({});
    const [optionValuesLoading, setOptionValuesLoading] = useState(false);
    const [hasInitializedOptionValues, setHasInitializedOptionValues] = useState(false);
    const optionValueGroupsRef = useRef<Record<string, OptionValueGroup>>({});

    useEffect(() => {
        optionValueGroupsRef.current = optionValueGroups;
    }, [optionValueGroups]);

    useEffect(() => {
        if (!menuId || existingMenu || hasRequestedMenus) {
            return;
        }

        setHasRequestedMenus(true);
        setIsFetchingMenu(true);

        const fetchPromise = dispatch(getMenus());
        Promise.resolve(fetchPromise).finally(() => {
            setIsFetchingMenu(false);
        });
    }, [dispatch, menuId, existingMenu, hasRequestedMenus]);

    useEffect(() => {
        if (existingMenu) {
            setIsFetchingMenu(false);
        }
    }, [existingMenu]);

    useEffect(() => {
        if (!menuOptionsState.loading && !menuOptionsState.loaded) {
            dispatch(getMenuOptions());
        }
    }, [dispatch, menuOptionsState.loading, menuOptionsState.loaded]);

    useEffect(() => {
        if (!menuId) {
            setDetailData(null);
            return;
        }

        setIsFetchingDetail(true);
        setDetailError(null);

        Promise.resolve(getMenuDetail(menuId))
            .then((response) => {
                const detail = extractMenuDetailFromResponse(response);
                if (detail && typeof detail === "object") {
                    setDetailData(detail);
                } else {
                    setDetailData(detail ?? null);
                }
            })
            .catch((error) => {
                console.error(error);
                const message =
                    typeof error === "string"
                        ? error
                        : "Không thể tải thông tin chi tiết của món.";
                setDetailError(message);
            })
            .finally(() => {
                setIsFetchingDetail(false);
            });
    }, [menuId]);

    const detailMenuData = useMemo(() => detailData ?? existingMenu, [detailData, existingMenu]);

    const initialValues = useMemo<MenuFormValues>(() => {
        if (!detailMenuData) {
            return {
                name: "",
                price: "",
                description: "",
                image: "",
                categories: [],
                available: true,
                optionSelections: [],
            };
        }

        const priceValue = getMenuPriceFromData(detailMenuData);
        const descriptionValue = getMenuDescriptionFromData(detailMenuData);
        const imageValue = getMenuImageFromData(detailMenuData);
        const categoryValue = getMenuCategoryFromData(detailMenuData, parentCategory);
        const optionIds = extractMenuOptionIdsFromMenu(detailMenuData)
            .map((id) => toComparableMenuOptionId(id))
            .filter((value): value is string => Boolean(value));

        return {
            name: getMenuNameFromData(detailMenuData),
            price: formatPriceValue(priceValue),
            description: descriptionValue,
            image: imageValue,
            categories: categoryValue,
            available: getMenuAvailabilityFromData(detailMenuData),
            optionSelections: optionIds,
        };
    }, [detailMenuData, parentCategory]);

    const existingImage = useMemo(
        () => getMenuImageFromData(detailMenuData ?? existingMenu),
        [detailMenuData, existingMenu]
    );

    const baseOptionChoices = useMemo(
        () => buildMenuOptionChoices(menuOptionsList),
        [menuOptionsList]
    );

    const detailOptionChoices = useMemo(
        () =>
            buildMenuOptionChoices(
                extractMenuOptionListFromMenu(detailMenuData ?? existingMenu ?? {})
            ),
        [detailMenuData, existingMenu]
    );

    const combinedOptionChoices = useMemo(() => {
        const map = new Map<string, MenuOptionSelectChoice>();
        [...baseOptionChoices, ...detailOptionChoices].forEach((choice) => {
            if (!map.has(choice.value)) {
                map.set(choice.value, choice);
            }
        });
        return Array.from(map.values());
    }, [baseOptionChoices, detailOptionChoices]);

    const choiceLookup = useMemo(() => {
        const map = new Map<string, MenuOptionSelectChoice>();
        combinedOptionChoices.forEach((choice) => {
            map.set(choice.value, choice);
        });
        return map;
    }, [combinedOptionChoices]);

    const groupedSelectOptions = useMemo(() => {
        type SelectOption = { value: string; label: string; isDisabled?: boolean };
        const groups = new Map<string, SelectOption[]>();
        const ungrouped: SelectOption[] = [];

        combinedOptionChoices.forEach((choice) => {
            const selectOption: SelectOption = {
                value: choice.value,
                label: choice.label,
                isDisabled: !getMenuOptionIsActive(choice.option),
            };

            if (choice.group) {
                if (!groups.has(choice.group)) {
                    groups.set(choice.group, []);
                }
                groups.get(choice.group)!.push(selectOption);
            } else {
                ungrouped.push(selectOption);
            }
        });

        if (groups.size === 0) {
            return ungrouped;
        }

        const grouped = Array.from(groups.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([label, options]) => ({ label, options }));

        if (ungrouped.length > 0) {
            grouped.push({ label: "Khác", options: ungrouped });
        }

        return grouped;
    }, [combinedOptionChoices]);

    const formik = useFormik<MenuFormValues>({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            if (!menuId) {
                setSubmitError("Không xác định được món cần cập nhật.");
                return;
            }

            setSubmitError(null);
            setIsSubmitting(true);
            const normalizedCategories = Array.from(
                new Set(values.categories.map((value) => value.trim()).filter(Boolean))
            );

            const availabilityPayload = {
                available: values.available,
                isAvailable: values.available,
                is_available: values.available,
                availability: values.available ? "available" : "unavailable",
                status: values.available ? "available" : "unavailable",
            };

            const payload: Record<string, any> = {
                name: values.name.trim(),
                price: Number(values.price),
                categories: normalizedCategories,
                ...availabilityPayload,
            };

            const trimmedDescription = values.description.trim();
            if (trimmedDescription.length > 0) {
                payload.description = trimmedDescription;
            }

            const trimmedImage = values.image.trim();
            if (trimmedImage.length > 0) {
                payload.image = trimmedImage;
            }

            const selectedChoices = values.optionSelections
                .map((value) => choiceLookup.get(value))
                .filter(Boolean) as MenuOptionSelectChoice[];

                if (selectedChoices.length > 0) {
                    const optionIds: (string | number)[] = [];
                    const optionPayloads: any[] = [];
                    const seen = new Set<string>();

                selectedChoices.forEach((choice) => {
                    if (seen.has(choice.value)) {
                        return;
                    }
                    seen.add(choice.value);
                    optionIds.push(choice.rawId ?? choice.value);
                    optionPayloads.push(choice.option);
                });

                    payload.optionIds = optionIds;
                    payload.options = optionPayloads;

                    const optionValuesPayload: Array<{
                        option_value_id: string | number;
                        price_modifier: number;
                        is_default: boolean;
                    }> = [];

                    optionIds.forEach((optionId) => {
                        const comparable = toComparableMenuOptionId(optionId);
                        if (!comparable) {
                            return;
                        }
                        const group = optionValueGroups[comparable];
                        if (!group) {
                            return;
                        }
                        group.values.forEach((value) => {
                            const price = Number(value.priceModifier);
                            optionValuesPayload.push({
                                option_value_id: value.id,
                                price_modifier: Number.isFinite(price) ? price : 0,
                                is_default: group.selectionType === "single" ? value.isDefault : false,
                            });
                        });
                    });

                    if (optionValuesPayload.length > 0) {
                        payload.menu_option_values = optionValuesPayload;
                    }
                } else {
                    payload.optionIds = [];
                    payload.options = [];
                }

            try {
                await dispatch(updateMenu({ menuId, data: payload })).unwrap();
                dispatch(
                    showAlertWithTimeout(
                        "success",
                        "Cập nhật món thành công",
                        "Thông tin món đã được lưu lại."
                    )
                );
                navigate("/menu");
            } catch (error) {
                const errorMessage =
                    typeof error === "string"
                        ? error
                        : "Không thể cập nhật món vào lúc này. Vui lòng thử lại.";
                setSubmitError(errorMessage);
                dispatch(
                    showAlertWithTimeout(
                        "danger",
                        "Cập nhật món thất bại",
                        errorMessage
                    )
                );
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const previewImage = useMemo(() => {
        const formImage = formik.values.image.trim();
        if (formImage.length > 0) {
            return formImage;
        }

        if (existingImage && existingImage.trim().length > 0) {
            return existingImage;
        }

        return DEFAULT_PRODUCT_IMAGE;
    }, [formik.values.image, existingImage]);

    const selectedOptionChoices = useMemo(
        () =>
            formik.values.optionSelections
                .map((value) => choiceLookup.get(value))
                .filter(Boolean) as MenuOptionSelectChoice[],
        [choiceLookup, formik.values.optionSelections]
    );

    const selectedSelectValues = useMemo(
        () =>
            selectedOptionChoices.map((choice) => ({
                value: choice.value,
                label: choice.label,
            })),
        [selectedOptionChoices]
    );

    const buildOptionValueGroup = useCallback(
        (choice: MenuOptionSelectChoice, values: any[]): OptionValueGroup => {
            const option = choice.option;
            const selectionType = getMenuOptionSelectionType(option);
            const name = getMenuOptionName(option) || choice.label;

            const mappedValues: OptionValueItem[] = values.map((value: any) => {
                const label =
                    value?.label ?? value?.name ?? value?.title ?? value?.displayName ?? "Lựa chọn";
                const rawAvailability =
                    value?.is_available ?? value?.isAvailable ?? value?.available ?? true;
                const availability =
                    typeof rawAvailability === "boolean"
                        ? rawAvailability
                        : typeof rawAvailability === "number"
                          ? rawAvailability !== 0
                          : String(rawAvailability).toLowerCase() !== "false";
                const price =
                    value?.price_modifier ?? value?.priceModifier ?? value?.price ?? 0;
                return {
                    id: value?.id ?? value?.option_value_id ?? value?.value_id,
                    label: String(label),
                    isAvailable: availability,
                    priceModifier: String(price ?? 0),
                    isDefault: Boolean(value?.is_default ?? value?.isDefault ?? false),
                };
            });

            if (selectionType === "single" && mappedValues.length > 0) {
                const hasDefault = mappedValues.some((item) => item.isDefault);
                if (!hasDefault) {
                    mappedValues[0].isDefault = true;
                }
            }

            return {
                optionId: choice.rawId ?? choice.value,
                name,
                selectionType,
                values: mappedValues,
            };
        },
        []
    );

    const buildGroupFromMenuOption = useCallback((option: any): OptionValueGroup | null => {
        const optionId = option?.id ?? option?.option_id ?? option?.optionId;
        const comparable = toComparableMenuOptionId(optionId);
        if (!comparable) {
            return null;
        }

        const selectionType = getMenuOptionSelectionType(option);
        const name = getMenuOptionName(option) || option?.name || option?.title || "Tùy chọn";
        const values = Array.isArray(option?.values) ? option.values : [];

        const mappedValues: OptionValueItem[] = values.map((value: any) => {
            const label =
                value?.label ?? value?.name ?? value?.title ?? value?.displayName ?? "Lựa chọn";
            const rawAvailability =
                value?.is_available ?? value?.isAvailable ?? value?.available ?? true;
            const availability =
                typeof rawAvailability === "boolean"
                    ? rawAvailability
                    : typeof rawAvailability === "number"
                      ? rawAvailability !== 0
                      : String(rawAvailability).toLowerCase() !== "false";
            const price =
                value?.price_modifier ?? value?.priceModifier ?? value?.price ?? 0;
            return {
                id: value?.id ?? value?.option_value_id ?? value?.value_id,
                label: String(label),
                isAvailable: availability,
                priceModifier: String(price ?? 0),
                isDefault: Boolean(value?.is_default ?? value?.isDefault ?? false),
            };
        });

        if (selectionType === "single" && mappedValues.length > 0) {
            const hasDefault = mappedValues.some((item) => item.isDefault);
            if (!hasDefault) {
                mappedValues[0].isDefault = true;
            }
        }

        return {
            optionId,
            name,
            selectionType,
            values: mappedValues,
        };
    }, []);

    useEffect(() => {
        if (!detailMenuData) {
            return;
        }

        const detailOptions = extractMenuOptionListFromMenu(detailMenuData ?? {});
        const groups: Record<string, OptionValueGroup> = {};

        detailOptions.forEach((option: any) => {
            const group = buildGroupFromMenuOption(option);
            const key = group ? toComparableMenuOptionId(group.optionId) : undefined;
            if (group && key) {
                groups[key] = group;
            }
        });

        const hasGroups = Object.keys(groups).length > 0;
        if (!hasGroups) {
            return;
        }

        const currentGroups = optionValueGroupsRef.current;
        const currentHasValues = Object.values(currentGroups).some(
            (group) => group.values.length > 0
        );

        if (!hasInitializedOptionValues || !currentHasValues) {
            setOptionValueGroups(groups);
            setHasInitializedOptionValues(true);
        }
    }, [buildGroupFromMenuOption, detailMenuData, hasInitializedOptionValues]);

    useEffect(() => {
        const selected = formik.values.optionSelections
            .map((value) => choiceLookup.get(value))
            .filter(Boolean) as MenuOptionSelectChoice[];
        const selectedSet = new Set(selected.map((choice) => choice.value));

        let didPrune = false;
        setOptionValueGroups((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((key) => {
                if (!selectedSet.has(key)) {
                    delete next[key];
                    didPrune = true;
                }
            });
            return didPrune ? next : prev;
        });

        const missing = selected.filter((choice) => {
            const group = optionValueGroupsRef.current[choice.value];
            return !group || group.values.length === 0;
        });
        if (missing.length === 0) {
            return;
        }

        let isMounted = true;
        setOptionValuesLoading(true);

        Promise.all(
            missing.map(async (choice) => {
                const optionId = choice.rawId ?? choice.value;
                const response = await getMenuOptionValues(optionId);
                const values = extractOptionValues(response?.data);
                return { key: choice.value, group: buildOptionValueGroup(choice, values) };
            })
        )
            .then((results) => {
                if (!isMounted) {
                    return;
                }
                setOptionValueGroups((prev) => {
                    const next = { ...prev };
                    results.forEach((result) => {
                        if (result?.group) {
                            const existingGroup = next[result.key];
                            next[result.key] = existingGroup
                                ? mergeOptionValueGroup(existingGroup, result.group)
                                : result.group;
                        }
                    });
                    return next;
                });
            })
            .catch((fetchError) => {
                console.error(fetchError);
            })
            .finally(() => {
                if (isMounted) {
                    setOptionValuesLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [formik.values.optionSelections, choiceLookup, buildOptionValueGroup]);

    const handlePriceModifierChange = (
        optionKey: string,
        valueId: string | number,
        nextValue: string
    ) => {
        setOptionValueGroups((prev) => {
            const group = prev[optionKey];
            if (!group) {
                return prev;
            }

            const updatedValues = group.values.map((value) =>
                String(value.id) === String(valueId)
                    ? { ...value, priceModifier: nextValue }
                    : value
            );

            return {
                ...prev,
                [optionKey]: { ...group, values: updatedValues },
            };
        });
    };

    const handleDefaultChange = (optionKey: string, valueId: string | number) => {
        setOptionValueGroups((prev) => {
            const group = prev[optionKey];
            if (!group) {
                return prev;
            }

            const updatedValues = group.values.map((value) => ({
                ...value,
                isDefault: String(value.id) === String(valueId),
            }));

            return {
                ...prev,
                [optionKey]: { ...group, values: updatedValues },
            };
        });
    };

    const optionGroupList = useMemo(() => {
        return Object.values(optionValueGroups).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    }, [optionValueGroups]);

    const categoryOptions = useMemo(() => {
        const unique = new Map<string, string>();
        categoriesList.forEach((category: any) => {
            const name = getCategoryDisplayName(category);
            if (name.trim().length > 0) {
                unique.set(name.trim(), name.trim());
            }
        });
        return Array.from(unique.values()).map((name) => ({
            value: name,
            label: name,
        }));
    }, [categoriesList]);

    const selectedCategoryValues = useMemo(
        () =>
            formik.values.categories.map((name) => ({
                value: name,
                label: name,
            })),
        [formik.values.categories]
    );

    const showLoadingState =
        (isFetchingMenu && !existingMenu) || (isFetchingDetail && !detailData);
    const showNotFoundState = !showLoadingState && menuId && !existingMenu;
    const missingMenuId = !menuId;

    return (
        <React.Fragment>
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <div className="back-button me-2">
                        <Link to="/menu">
                            <i className="mdi mdi-arrow-left"></i>
                        </Link>
                    </div>

                    <div className="page-heading">
                        <h6 className="mb-0">Chỉnh sửa món</h6>
                    </div>

                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="profile-wrapper-area py-3">
                        {showLoadingState ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2 mb-0">Đang tải thông tin món...</p>
                            </div>
                        ) : missingMenuId ? (
                            <div className="alert alert-danger" role="alert">
                                Không tìm thấy mã món hợp lệ để chỉnh sửa.
                            </div>
                        ) : showNotFoundState ? (
                            <div className="alert alert-warning" role="alert">
                                Không tìm thấy món bạn yêu cầu. Vui lòng quay lại danh sách.
                            </div>
                        ) : (
                            <React.Fragment>
                                <div className="card mb-3">
                                    <div className="card-body d-flex align-items-center">
                                        <div className="me-3">
                                            <img
                                                src={previewImage}
                                                alt={formik.values.name || "Ảnh món"}
                                                className="rounded"
                                                style={{ width: "80px", height: "80px", objectFit: "cover" }}
                                            />
                                        </div>
                                        <div>
                                            <h6 className="mb-1">{formik.values.name || "Tên món"}</h6>
                                            <span
                                                className={
                                                    formik.values.available
                                                        ? "badge bg-success-subtle text-success"
                                                        : "badge bg-danger-subtle text-danger"
                                                }
                                            >
                                                {formik.values.available ? "Còn món" : "Hết món"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="card user-data-card">
                                    <div className="card-body">
                                        {submitError && (
                                            <div className="alert alert-danger" role="alert">
                                                {submitError}
                                            </div>
                                        )}

                                        <form onSubmit={formik.handleSubmit} noValidate>
                                            <div className="mb-3">
                                                <div className="title mb-2">
                                                    <i className="mdi mdi-notebook-outline"></i>
                                                    <span>Tên món</span>
                                                </div>
                                                <input
                                                    className={`form-control ${
                                                        formik.touched.name && formik.errors.name
                                                            ? "is-invalid"
                                                            : ""
                                                    }`}
                                                    type="text"
                                                    name="name"
                                                    value={formik.values.name}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="Nhập tên món"
                                                    disabled={isSubmitting}
                                                />
                                                {formik.touched.name && formik.errors.name && (
                                                    <div className="invalid-feedback d-block">
                                                        {formik.errors.name}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mb-3">
                                                <div className="title mb-2">
                                                    <i className="mdi mdi-currency-usd"></i>
                                                    <span>Giá</span>
                                                </div>
                                                <input
                                                    className={`form-control ${
                                                        formik.touched.price && formik.errors.price
                                                            ? "is-invalid"
                                                            : ""
                                                    }`}
                                                    type="number"
                                                    name="price"
                                                    min="0"
                                                    step="1000"
                                                    value={formik.values.price}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="Nhập giá món"
                                                    disabled={isSubmitting}
                                                />
                                                {formik.touched.price && formik.errors.price && (
                                                    <div className="invalid-feedback d-block">
                                                        {formik.errors.price}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mb-3">
                                                <div className="title mb-2">
                                                    <i className="mdi mdi-format-align-left"></i>
                                                    <span>Mô tả</span>
                                                </div>
                                                <textarea
                                                    className="form-control"
                                                    name="description"
                                                    rows={3}
                                                    value={formik.values.description}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="Thêm mô tả cho món"
                                                    disabled={isSubmitting}
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <div className="title mb-2">
                                                    <i className="mdi mdi-image-outline"></i>
                                                    <span>Ảnh (URL)</span>
                                                </div>
                                                <input
                                                    className={`form-control ${
                                                        formik.touched.image && formik.errors.image
                                                            ? "is-invalid"
                                                            : ""
                                                    }`}
                                                    type="url"
                                                    name="image"
                                                    value={formik.values.image}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="https://example.com/your-image.jpg"
                                                    disabled={isSubmitting}
                                                />
                                                {formik.touched.image && formik.errors.image && (
                                                    <div className="invalid-feedback d-block">
                                                        {formik.errors.image as string}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mb-3">
                                                <div className="title mb-2">
                                                    <i className="mdi mdi-shape-outline"></i>
                                                    <span>Danh mục</span>
                                                </div>
                                                <CreatableSelect
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    options={categoryOptions}
                                                    value={selectedCategoryValues}
                                                    classNamePrefix="react-select"
                                                    placeholder="Chọn hoặc nhập danh mục"
                                                    onChange={(selected) => {
                                                        const selections = Array.isArray(selected)
                                                            ? selected.map((item: any) => item.value)
                                                            : [];
                                                        formik.setFieldValue("categories", selections);
                                                    }}
                                                    onBlur={() => formik.setFieldTouched("categories", true)}
                                                    isDisabled={isSubmitting}
                                                />
                                                {formik.touched.categories && formik.errors.categories && (
                                                    <div className="invalid-feedback d-block">
                                                        {formik.errors.categories as string}
                                                    </div>
                                                )}
                                            </div>

                                        <div className="mb-3">
                                            <div className="title mb-2">
                                                <i className="mdi mdi-playlist-check"></i>
                                                <span>Tùy chọn áp dụng</span>
                                            </div>
                                            <Select
                                                isMulti
                                                closeMenuOnSelect={false}
                                                options={groupedSelectOptions as any}
                                                value={selectedSelectValues as any}
                                                classNamePrefix="react-select"
                                                isLoading={menuOptionsState.loading || isFetchingDetail}
                                                placeholder="Chọn tùy chọn cho món"
                                                onChange={(selected) => {
                                                    const selections = Array.isArray(selected)
                                                        ? selected.map((item: any) => item.value)
                                                        : [];
                                                    formik.setFieldValue("optionSelections", selections);
                                                }}
                                                onBlur={() => formik.setFieldTouched("optionSelections", true)}
                                                isDisabled={isSubmitting}
                                            />
                                            {menuOptionsState.error && (
                                                <p className="text-danger small mt-2">
                                                    {menuOptionsState.error}
                                                </p>
                                            )}
                                            {detailError && (
                                                <p className="text-warning small mt-2">
                                                    {detailError}
                                                </p>
                                            )}
                                            {selectedSelectValues.length > 0 && (
                                                <div className="mt-2">
                                                    {selectedSelectValues.map((item) => (
                                                        <span
                                                            key={item.value}
                                                            className="badge bg-primary-subtle text-primary me-1 mb-1"
                                                        >
                                                            {item.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {selectedSelectValues.length > 0 && (
                                            <div className="mb-3">
                                                <div className="title mb-2">
                                                    <i className="mdi mdi-format-list-bulleted"></i>
                                                    <span>Cấu hình lựa chọn</span>
                                                </div>
                                                {optionValuesLoading && (
                                                    <div className="text-muted small mb-2">
                                                        Đang tải lựa chọn...
                                                    </div>
                                                )}
                                                {optionGroupList.length === 0 && !optionValuesLoading && (
                                                    <div className="alert alert-warning">
                                                        Chưa có lựa chọn nào cho các tùy chọn đã chọn.
                                                    </div>
                                                )}
                                                {optionGroupList.map((group) => (
                                                    <div key={group.optionId} className="card mb-2">
                                                        <div className="card-body p-2">
                                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                                <div className="fw-semibold">{group.name}</div>
                                                                <span className="badge bg-info-subtle text-info">
                                                                    {group.selectionType === "single"
                                                                        ? "Chọn 1"
                                                                        : "Chọn nhiều"}
                                                                </span>
                                                            </div>
                                                            {group.values.length === 0 ? (
                                                                <div className="text-muted small">
                                                                    Chưa có lựa chọn nào.
                                                                </div>
                                                            ) : (
                                                                <div className="table-responsive">
                                                                    <table className="table table-sm mb-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Lựa chọn</th>
                                                                                <th className="text-end">Giá cộng thêm</th>
                                                                                {group.selectionType === "single" && (
                                                                                    <th className="text-center">
                                                                                        Mặc định
                                                                                    </th>
                                                                                )}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {group.values.map((value) => (
                                                                                <tr key={value.id}>
                                                                                    <td>
                                                                                        <span className="fw-medium">
                                                                                            {value.label}
                                                                                        </span>
                                                                                        {!value.isAvailable && (
                                                                                            <span className="badge bg-secondary-subtle text-secondary ms-2">
                                                                                                Hết
                                                                                            </span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="text-end">
                                                                                        <input
                                                                                            type="number"
                                                                                            step="0.01"
                                                                                            className="form-control form-control-sm text-end"
                                                                                            value={value.priceModifier}
                                                                                            onChange={(event) =>
                                                                                                handlePriceModifierChange(
                                                                                                    toComparableMenuOptionId(
                                                                                                        group.optionId
                                                                                                    ) ?? String(group.optionId),
                                                                                                    value.id,
                                                                                                    event.target.value
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </td>
                                                                                    {group.selectionType === "single" && (
                                                                                        <td className="text-center">
                                                                                            <input
                                                                                                type="radio"
                                                                                                name={`default-${group.optionId}`}
                                                                                                checked={value.isDefault}
                                                                                                onChange={() =>
                                                                                                    handleDefaultChange(
                                                                                                        toComparableMenuOptionId(
                                                                                                            group.optionId
                                                                                                        ) ?? String(group.optionId),
                                                                                                        value.id
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        </td>
                                                                                    )}
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="mb-4">
                                            <div className="title mb-2">
                                                <i className="mdi mdi-toggle-switch-outline"></i>
                                                <span>Trạng thái món</span>
                                                </div>
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="menu-availability-switch"
                                                        name="available"
                                                        checked={formik.values.available}
                                                        onChange={(event) =>
                                                            formik.setFieldValue(
                                                                "available",
                                                                event.currentTarget.checked
                                                            )
                                                        }
                                                        onBlur={formik.handleBlur}
                                                        disabled={isSubmitting}
                                                    />
                                                    <label className="form-check-label" htmlFor="menu-availability-switch">
                                                        {formik.values.available ? "Còn món" : "Hết món"}
                                                    </label>
                                                </div>
                                            </div>

                                            <button
                                                className="btn btn-primary btn-lg w-100"
                                                type="submit"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? "Đang lưu..." : "Lưu thay đổi"}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default EditMenu;
